import React from 'react'
import { useExpanded, useGroupBy, useSortBy, useTable } from 'react-table'

import { FaPlusCircle, FaMinusCircle } from 'react-icons/fa'

import styled from 'styled-components'
import PageButtons from '../../../../components/PageButtons'

export default function TableRolesDisplay({
  columns,
  data,
  totalPage,
  currentPage,
  pageSize,
  rowCount,
  setNewPageSize,
  setCurrentPage,
  groupedByCol,
  fetchData,
}) {
  const defaultExpanded = React.useMemo(() => {
    const expanded = {}
    data.forEach((row, index) => {
      expanded[`${groupedByCol}:${row[groupedByCol]}`] = true
    })
    return expanded
  }, [data])

  const startIndex = (currentPage - 1) * pageSize + 1
  const endIndex = Math.min(currentPage * pageSize, rowCount)

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable(
      {
        columns,
        data,
        initialState: {
          groupBy: [groupedByCol],
          expanded: defaultExpanded,
        },
      },
      useGroupBy,
      useSortBy,
      useExpanded
    )

  const handleChangePageSize = (newPageSize) => {
    setNewPageSize(newPageSize)
    setCurrentPage(1)
  }

  const handleByIndex = (page) => {
    setCurrentPage(page)
  }

  return (
    <>
      <StyledTable {...getTableProps()}>
        <thead>
          {headerGroups.map((headerGroup, headerGroupIndex) => (
            <tr key={headerGroupIndex} {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column, columnIndex) => (
                <th
                  key={columnIndex}
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                >
                  {column.render('Header')}
                  <span>
                    {column.isSorted ? (column.isSortedDesc ? ' ▼' : ' ▲') : ''}
                  </span>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map((row, rowIndex) => {
            prepareRow(row)
            return (
              <tr key={rowIndex} {...row.getRowProps()}>
                {row.cells.map((cell, cellIndex) => {
                  return (
                    <td key={cellIndex} {...cell.getCellProps()}>
                      {cell.isGrouped ? (
                        // If it's a grouped cell, add an expander and row count
                        <div className="group">
                          <span {...row.getToggleRowExpandedProps()}>
                            {row.isExpanded ? (
                              <FaMinusCircle />
                            ) : (
                              <FaPlusCircle />
                            )}
                          </span>
                          {cell.render('Cell')}
                        </div>
                      ) : cell.isAggregated ? (
                        cell.render('Aggregated')
                      ) : cell.isPlaceholder ? null : (
                        cell.render('Cell')
                      )}
                    </td>
                  )
                })}
              </tr>
            )
          })}
        </tbody>
      </StyledTable>
      <Pagination>
        <PageButtons
          totalPages={totalPage}
          currentPage={currentPage}
          onPageClick={handleByIndex}
          maxWidth={300}
        />
        <select
          value={pageSize}
          onChange={(e) => {
            handleChangePageSize(Number(e.target.value))
          }}
        >
          {[10, 20, 30, 40, 50].map((size) => (
            <option key={size} value={size}>
              View {size}
            </option>
          ))}
        </select>
        <span>{`${startIndex} - ${endIndex} of ${rowCount}`}</span>
      </Pagination>
    </>
  )
}

const StyledTable = styled.table`
  width: 100%;
  background: #fff;
  padding: 0.5rem 0;
  padding-top: 0;
  border-collapse: separate;
  border-spacing: 0;
  text-align: center;

  th,
  tr,
  td {
    border: 1px solid #eeeef6;
  }

  thead {
    position: sticky;
    top: 0;
    z-index: 1;
    background: #fff;

    th {
      border-bottom: 1px solid #eeeef6;
      max-width: 100px;
      font-size: 1rem;
      padding: 0.75rem 0;
      padding-top: 0.75rem;
      font-weight: 500;
    }
  }

  tbody {
    .group {
      display: flex;
      align-items: center;
      padding: 10px;
      border-radius: 5px;
      margin-bottom: 5px;
      width: 100%;

      span {
        margin-right: 10px;
      }
    }

    tr {
      word-break: break-all;

      &:hover {
        background: #e8e8e8;
        transition: all 0.3s ease;
        cursor: pointer;
      }
    }

    td {
      max-width: 210px;
      padding: 0.5rem;
      font-size: 1rem;
      font-weight: 300;
    }

    tr:last-child td {
      border-bottom: 0;
    }

    tr:first-child td {
      padding: 1.25rem 0.5rem;
    }
  }
`

const Pagination = styled.div`
  position: sticky;
  bottom: 0;
  left: 0;
  width: 100%;

  background: #f9f9fc;
  display: flex;
  align-items: center;

  select {
    height: 2rem;
    background: #ffffff;
    border: 0.5px solid #aeb3c9;
    margin: 0 0.75rem;
    font-size: 0.75rem;
    padding: 0 0.5rem;
    outline: none;
  }

  span {
    font-size: 0.75rem;
    font-weight: 300;
    color: #0e1324;
  }
`
